import clsx from 'clsx'
import type { DataStatus, ISODate } from '@app/shared'

import { agoPhrase, countPhrase } from './labels'
import { indexPercent, indexTone } from './model'
import { ActionButton } from './parts'

// ============================================================================
// DATA & AI — the 462px panel: which dataset the app is on, how much of it is
// searchable and what is holding the rest up, and the way out of the product
// entirely.
//
// EXPORT IS THE ANTI-LOCK-IN PROMISE and it is deliberately the least dramatic
// row on the screen: Markdown entries, JSON metadata, original audio. It also
// exports the REAL data while demo mode is on, which is the only behaviour that
// makes sense — nobody wants a backup of the sample dataset.
// ============================================================================

export function DataAndAI({
  status,
  today,
  nowMinutes,
}: {
  status: DataStatus
  today: ISODate
  nowMinutes: number
}) {
  const demo = status.mode === 'demo'

  return (
    <div className="flex flex-1 flex-col gap-[13px] rounded-card border border-line bg-surface px-4 py-[14px]">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <div className={clsx('text-[12px] font-medium leading-none', demo && 'text-warning-2')}>
            {demo ? 'Demo data' : 'Real data'}
          </div>
          <div className="text-[10.5px] font-normal leading-[1.4] text-microcopy">
            {demo
              ? 'Sample dataset is showing everywhere in the app'
              : 'Off shows the demo dataset — useful for screenshots'}
          </div>
        </div>
        <DataModeSwitch demo={demo} />
      </div>

      <div className="h-px bg-fill-2" aria-hidden />

      <IndexBlock index={status.index} today={today} nowMinutes={nowMinutes} />

      <div className="h-px bg-fill-2" aria-hidden />

      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <div className="text-[12px] font-medium leading-none">Export everything</div>
          <div className="text-[10.5px] font-normal leading-[1.4] text-microcopy">
            {demo
              ? 'Exports your real data even while demo mode is on'
              : 'Markdown entries, JSON metadata, original audio — no lock-in'}
          </div>
        </div>
        <ActionButton
          label="Export"
          waitingFor="The export bundle arrives with V6, once there is more than a journal to put in it."
        />
      </div>
    </div>
  )
}

/**
 * THE SWITCH REPORTS; IT DOES NOT YET DECIDE. In this codebase the dataset is
 * chosen by configuration — a build with Supabase and API keys is real, a build
 * without is demo (lib/data/index.ts) — so there is nothing for a runtime toggle
 * to flip. It renders as designed, states its position to a screen reader, and
 * says in its title what actually changes it. A switch that moved and changed
 * nothing would be the exact dishonesty this screen exists to refuse.
 */
function DataModeSwitch({ demo }: { demo: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!demo}
      aria-disabled="true"
      aria-label="Real data"
      title="Which dataset the app runs on is set by this build's configuration, not by a switch: with a Supabase project and an API base URL it is real, without them it is the demo dataset. A runtime toggle is not built."
      onClick={(e) => e.preventDefault()}
      className={clsx(
        'flex h-[22px] w-[38px] flex-none cursor-not-allowed rounded-pill p-[3px]',
        'transition-colors duration-[var(--dur-base)] ease-[var(--ease-out)]',
        demo ? 'justify-start bg-warning-line' : 'justify-end bg-ink',
      )}
    >
      <span className="size-4 rounded-full bg-surface" aria-hidden />
    </button>
  )
}

function IndexBlock({
  index,
  today,
  nowMinutes,
}: {
  index: DataStatus['index']
  today: ISODate
  nowMinutes: number
}) {
  const percent = indexPercent(index)
  const tone = indexTone(index)

  // "63 waiting on Canvas · rest current as of 9 min ago" — and when the app does
  // not know when the indexed part was last confirmed, that clause is dropped
  // rather than filled in with the time of the render.
  const blocking = index.blockedBy ? `${index.pending} waiting on ${index.blockedBy}` : null
  const asOf = index.currentAsOf
    ? `rest current as of ${agoPhrase(index.currentAsOf, today, nowMinutes)}`
    : null
  const line = [blocking, asOf].filter(Boolean).join(' · ')

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-[10px]">
        <div className="flex-1 text-[12px] font-medium leading-none">Search index</div>
        <div className="text-[10.5px] font-normal leading-none text-microcopy">
          {countPhrase(index.itemsIndexed)} items indexed
        </div>
      </div>

      <div
        className="flex h-[5px] overflow-hidden rounded-pill bg-fill"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Share of known items that are searchable"
      >
        <div
          className={clsx(tone === 'warning' ? 'bg-warning-dot' : 'bg-success-dot')}
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="flex items-center gap-2">
        {line && <div className="text-[10.5px] font-normal leading-none text-microcopy">{line}</div>}
        <button
          type="button"
          aria-disabled="true"
          title="Rebuilding the index needs the index, which arrives with V4 (the AI layer)."
          onClick={(e) => e.preventDefault()}
          className="ml-auto cursor-not-allowed text-[10.5px] font-semibold leading-none text-success-dot"
        >
          Rebuild
        </button>
      </div>
    </div>
  )
}
